import type { FilterValue, FilterNegations } from '@/types/filters.types';
import { isDateRange, isSelect } from '@/types/filters.types';

/**
 * CANONICAL FILTER REGISTRY
 *
 * These keys and values are shared between React, the Laravel API, and the
 * legacy PHP system. They are stored permanently in email_segments.filter_conditions.
 *
 * RULES:
 * 1. NEVER rename an existing key — saved dynamic lists reference them forever
 * 2. NEVER reorder or remove index map values — "0" must always mean "Added"
 * 3. NEVER change a value's meaning — if "3" means "Frozen", it means Frozen forever
 * 4. New filters: pick a kebab-case key, add to this registry, add to FilterNormalizer,
 *    add to ListFilterService, add to segmentFilters() API response
 * 5. All filter keys are kebab-case (plan-type, not planType or plan_type)
 * 6. All ID values are STRING arrays (["708", "5"], not [708, 5])
 *
 * ADDING A NEW FILTER:
 *   Frontend: Add FilterConfig entry with the new key to the relevant page config
 *   Backend:  Add case to FilterNormalizer::mapFilterEntry()
 *   Backend:  Add WHERE logic to ListFilterService::apply*Filters()
 *   Backend:  Add options group to MarketingController::segmentFilters()
 *   Backend:  Add to FilterNormalizer::applyNegation() if it supports exclude
 *   Test:     Create a dynamic list, save, reload, verify filter still populated
 *
 * @see api/app/Services/FilterNormalizer.php — backend translation
 * @see api/app/Services/ListFilterService.php — WHERE clause application
 * @see api/app/Http/Controllers/V5/MarketingController.php — segmentFilters() options
 */

// ─── Index-based status filters (value = numeric index string) ────────────────
// NEVER reorder, remove, or change these mappings.

export const MEMBERSHIP_STATUS_INDEX: Record<string, string> = {
  '0': 'Added',
  '1': 'Past Due',
  '2': 'Cancelled',
  '3': 'Frozen',
  '4': 'Set to Cancel',
};

export const PAYMENT_STATUS_INDEX: Record<string, string> = {
  '0': 'Manually Entered',
  '1': 'Pending',
  '2': 'Settled',
  '3': 'Voided',
  '4': 'Declined',
  '5': 'Refund',
};

export const PACKAGE_STATUS_INDEX: Record<string, string> = {
  '0': 'Active',
  '1': 'Declined',
  '2': 'Inactive',
};

// `package-remaining` (SCRUM-2371), the "Remaining Products" filter: does the
// package still hold a product or service the guest can use (unused, not
// deleted, not expired). The rule is ListFilterService::applyPackageRemainingFilter().
// Labels may change; the index values may not.
export const PACKAGE_REMAINING_INDEX: Record<string, string> = {
  '0': 'Products remaining',
  '1': 'None remaining',
};

// ─── Label-string status filters (value = exact label text) ──────────────────

export const APPOINTMENT_STATUS_LABELS = [
  'Scheduled',
  'Contact Attempted',
  'Confirmed',
  'Waiting List',
  'No Show',
  'Cancelled',
  'Reschedule Pending',
  'Checked In',
  'Checked Back',
  'Checked Out',
] as const;

// ─── Combined index label map (for rendering numeric indices as display text) ─

export const FILTER_INDEX_LABELS: Record<string, Record<string, string>> = {
  'membership-status': MEMBERSHIP_STATUS_INDEX,
  'payment-status': PAYMENT_STATUS_INDEX,
  'package-status': PACKAGE_STATUS_INDEX,
  'package-remaining': PACKAGE_REMAINING_INDEX,
};

// ─── Canonical filter keys (for reference / autocomplete) ────────────────────
// These are the ONLY valid filter keys across the entire application.

export const FILTER_KEYS = {
  // ID-based (values = string arrays of database IDs)
  FACILITY: 'facility',
  PLAN_TYPE: 'plan-type',
  MEMBERSHIP_TYPE: 'membership-type',
  DOCTOR: 'doctor',
  COORDINATOR: 'coordinator',
  LEAD_SOURCE: 'lead-source',
  LEAD_SOURCE_GROUP: 'lead-source-filtersgroup',
  PLAN_STAGES: 'plan-stages',
  PLAN_STATUS: 'plan-status',
  MATERIAL: 'material',
  PAYMENT_TYPE: 'payment-type',
  PAYMENT_SOURCE: 'payment-source',
  APPT_TYPE: 'appt-type',
  USERS: 'users',
  PRODUCT: 'product',
  PACKAGE_TYPE: 'package-type',

  // Index-based enums (values = numeric index strings)
  MEMBERSHIP_STATUS: 'membership-status',
  PAYMENT_STATUS: 'payment-status',
  PACKAGE_STATUS: 'package-status',
  PACKAGE_REMAINING: 'package-remaining',

  // Label-string enums
  APPOINTMENT_STATUS: 'appointment-status',

  // Range / date
  DATERANGE: 'daterange',
  AGE: 'age',

  // Boolean / simple
  GENDER: 'gender',
  MEMBER_STATUS: 'member-status',

  // UTM (values = string arrays of raw utm_source values from contacts_record)
  UTM_SOURCE: 'utm-source',

  // Marketplace orders — source `integration_order` (SCRUM-2207)
  INTEGRATION: 'integration', // vendor keys: 'axiom', 'axiom_vials', 'rmm', …
  ORDER_STATUS: 'order-status', // fulfillmentStatus strings as stored
  ORDER_PRESCRIBER: 'order-prescriber', // prescriber names; '__none__' = none
  ORDERED_BY: 'ordered-by', // userID strings; '0' = not recorded
  INCLUDE_CANCELLED: 'include-cancelled', // ['1'] keeps cancelled orders

  // Gift cards — source `giftcard`
  ONLINE_SALE: 'online-sale', // 'unclaimed' / 'claimed' online cards

  // Negation meta-key
  FILTER_PLUS: 'filter-plus',
} as const;

// ─── Filter option builders (generate options from index maps) ────────────────

export function buildIndexOptions(indexMap: Record<string, string>) {
  return Object.entries(indexMap).map(([value, label]) => ({ value, label }));
}

/**
 * Source buckets for the payments Source filter — WHICH CLIENT took the payment.
 *
 * Derived from `api_request_metadata`: v4 writes a row there only when the
 * calling client reported metadata, and only the patient apps report any, so a
 * payment with NO row was entered in Prospera by a staff member. The canonical
 * SQL is `ListFilterService::applyPaymentSourceFilter()`; these values must match it
 * and the `payment-source` group in `MarketingController::segmentFilters()`.
 *
 * These values are PERMANENT (rule 3 above) — saved dynamic lists store them.
 *
 * The buckets are EXHAUSTIVE by construction: every payment falls in exactly
 * one, so ticking all five matches the same rows as ticking none. 'unreported'
 * is what keeps that true — it holds payments that came through the API but
 * whose client did not send a `platform`. It is deliberately NOT folded into
 * 'webapp': a blank platform means "we were not told", and defaulting it to web
 * would misattribute the tail of old app installs to the web app.
 */
export const PAYMENT_SOURCE_OPTIONS: { value: string; label: string }[] = [
  { value: 'prospera', label: 'Prospera' },
  { value: 'ios', label: 'iOS' },
  { value: 'android', label: 'Android' },
  { value: 'webapp', label: 'Web App' },
  { value: 'unreported', label: 'App (not reported)' },
];

export const MEMBERSHIP_STATUS_OPTIONS = buildIndexOptions(MEMBERSHIP_STATUS_INDEX);
export const PAYMENT_STATUS_OPTIONS = buildIndexOptions(PAYMENT_STATUS_INDEX);
export const PACKAGE_STATUS_OPTIONS = buildIndexOptions(PACKAGE_STATUS_INDEX);
export const PACKAGE_REMAINING_OPTIONS = buildIndexOptions(PACKAGE_REMAINING_INDEX);
export const APPOINTMENT_STATUS_OPTIONS = APPOINTMENT_STATUS_LABELS.map((label) => ({
  value: label,
  label,
}));

// Canonical `member-status` values — is the patient a member right now. The
// same two the tasks list uses, so a saved dynamic list means the same thing
// on every source (appointments, patients, tasks).
export const MEMBER_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'member', label: 'Member' },
  { value: 'non-member', label: 'Non-Member' },
];

// ─── Unified filter payload builder ──────────────────────────────────────────

/**
 * Convert filter state (legacy keys + values) into the unified `filtersArray` JSON
 * param expected by all API endpoints.
 *
 * This is the ONE function used to send filters from React to the API.
 * It replaces all per-page `*ParamsFromValues()` builders.
 */
export function buildFiltersPayload(
  values: Record<string, FilterValue>,
  negations: FilterNegations = {},
): { filtersArray: string } {
  const fa: Record<string, unknown> = {};

  for (const [key, val] of Object.entries(values)) {
    if (key === 'search') continue;
    if (isSelect(val) && val.length > 0) {
      fa[key] = val;
    } else if (isDateRange(val) && (val.from || val.to)) {
      fa[key] = [val.from || '', val.to || ''];
    }
  }

  const filterPlus = negatedFilterKeys(fa, negations);
  if (filterPlus.length > 0) {
    fa[FILTER_KEYS.FILTER_PLUS] = filterPlus;
  }

  return { filtersArray: JSON.stringify(fa) };
}

/**
 * The filter keys whose +/- toggle is set to negative, restricted to keys that
 * actually carry a value (a negated empty filter is a no-op).
 *
 * This LIST of keys is the v5 wire format for negations — the same shape
 * marketing.service sends as top-level `filterPlus`, and the shape
 * FilterNormalizer::negatedKeys() treats as canonical. Do NOT send a
 * `{ key: boolean }` map here: the legacy pane stores that shape with the
 * opposite meaning (false == negative), so a map is ambiguous on the wire.
 */
export function negatedFilterKeys(
  emittedValues: Record<string, unknown>,
  negations: FilterNegations = {},
): string[] {
  return Object.keys(emittedValues).filter(
    (key) => key !== FILTER_KEYS.FILTER_PLUS && negations[key] === true,
  );
}

/**
 * The `filterConditions` shape the marketing modal / dynamic lists consume:
 * `{ source_type, filtersArray (no filter-plus inside), filterPlus: string[] }`.
 * Every list page used to hand-roll this from buildFiltersPayload's JSON.
 */
export function buildMarketingFilterConditions(
  sourceType: string,
  values: Record<string, FilterValue>,
  negations: FilterNegations = {},
): { source_type: string; filtersArray: Record<string, unknown>; filterPlus: string[] } {
  const { filtersArray: faStr } = buildFiltersPayload(values, negations);
  const parsed = JSON.parse(faStr) as Record<string, unknown>;
  const raw = parsed[FILTER_KEYS.FILTER_PLUS];
  delete parsed[FILTER_KEYS.FILTER_PLUS];
  const filterPlus = Array.isArray(raw) ? raw.filter((k): k is string => typeof k === 'string') : [];
  return { source_type: sourceType, filtersArray: parsed, filterPlus };
}

/**
 * Extract the search value from filter state (convenience for API calls
 * that still accept `search` as a separate param alongside filtersArray).
 */
export function extractSearch(values: Record<string, FilterValue>): string {
  const v = values['search'];
  return isSelect(v) && v.length > 0 ? v[0] : '';
}

/**
 * Universal API params builder. Every list page calls this ONE function.
 *
 * Combines filters, search, pagination, and sort into the single params
 * object that every API endpoint accepts. The backend reads `filtersArray`
 * via FilterNormalizer::fromMarketingFormat() — one code path, one result.
 */
export interface ApiListParams {
  filtersArray: string;
  search?: string;
  page?: number;
  perPage?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  [key: string]: unknown;
}

/**
 * Generic over `base` so the caller's own keys survive into the return type.
 *
 * It used to return a bare `ApiListParams`, whose page/perPage/sortBy/sortDir
 * are all optional. Callers pass those from component state — always present —
 * and then hand the result to a service whose params type requires them, so
 * every such call was a TS2345 saying 'number | undefined is not assignable to
 * number'. The values were never actually missing; the signature was throwing
 * away what it knew.
 */
export function buildApiParams<B extends Record<string, unknown> = Record<string, never>>(
  values: Record<string, FilterValue>,
  negations: FilterNegations = {},
  base: B = {} as B,
): ApiListParams & B {
  const { filtersArray } = buildFiltersPayload(values, negations);
  const search = extractSearch(values);
  const params = { ...base, filtersArray } as ApiListParams & B;
  if (search) params.search = search;
  return params;
}
